import React, { useRef, useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Link2, CheckCircle2, LogOut, Camera, Save } from "lucide-react";
import { GlassField } from "@/components/GlassBits";
import { Avatar } from "@/components/community/ChatList";
import { GlassButton } from "@/components/GlassBits";
import { useUserAuth } from "@/context/UserAuthContext";

export const UserAccount = () => {
  const { profile, refresh, signOut } = useUserAuth();
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState(profile?.name || "");
  const [bio, setBio] = useState(profile?.bio || "");
  const [photo, setPhoto] = useState(profile?.photo || null);
  const [saving, setSaving] = useState(false);
  const photoRef = useRef(null);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await userApi.post("/community/media", fd);
      await userApi.patch("/community/profile", { photo_url: data.url });
      setPhoto(data.url);
      await refresh();
      toast.success("Foto actualizada");
    } catch (e) {
      toast.error(apiError(e, "No se pudo subir la foto"));
    } finally { setSaving(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await userApi.patch("/community/profile", { name, bio });
      await refresh();
      toast.success("Perfil guardado");
    } catch (e) {
      toast.error(apiError(e, "No se pudo guardar el perfil"));
    } finally { setSaving(false); }
  };

  const getCode = async () => {
    setBusy(true);
    try {
      const { data } = await userApi.post("/user/telegram/link-code");
      setCode(data);
      toast.success("Código listo: mándalo al bot en Telegram");
    } catch (e) {
      toast.error(apiError(e, "No se pudo generar el código"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-xl" data-testid="user-account">
      <div className="glass-card p-6" data-testid="profile-editor">
        <div className="flex items-center gap-4 mb-4">
          <Avatar person={{ name, photo }} size={64} />
          <div>
            <button data-testid="profile-photo-btn" onClick={() => photoRef.current?.click()}
              className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5" /> Cambiar foto
            </button>
            <input ref={photoRef} type="file" accept="image/*" className="hidden"
              data-testid="profile-photo-input" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
          </div>
        </div>
        <div className="space-y-3">
          <GlassField label="Nombre" value={name} data-testid="profile-name-input"
            onChange={(e) => setName(e.target.value)} />
          <GlassField label="Biografía" value={bio} data-testid="profile-bio-input"
            placeholder="Cuéntale a la comunidad a qué te dedicas"
            onChange={(e) => setBio(e.target.value)} />
        </div>
        <GlassButton tone="red" className="mt-4" icon={Save} loading={saving}
          data-testid="profile-save-btn" onClick={saveProfile}>Guardar perfil</GlassButton>
      </div>

      <div className="glass-card p-6 space-y-3">
        {[["Nombre", profile?.name || "—"], ["Correo", profile?.email],
          ["Acceso", profile?.auth_provider === "google" ? "Google" : "Correo y contraseña"],
          ["Créditos", profile?.credits]].map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm border-b border-white/8 pb-2">
            <span className="text-white/50">{k}</span>
            <span className="text-white">{String(v ?? "—")}</span>
          </div>
        ))}
      </div>

      <div className="glass-card p-6">
        <div className="flex items-center gap-2 text-white font-semibold mb-2">
          <Link2 className="w-4 h-4 text-red-400" /> Telegram
        </div>
        {profile?.telegram_linked ? (
          <div className="flex items-center gap-2 text-sm text-emerald-400" data-testid="telegram-linked">
            <CheckCircle2 className="w-4 h-4" /> Vinculado a {profile.telegram_id} — mismo saldo en la web y en el bot
          </div>
        ) : (
          <>
            <p className="text-xs text-white/55 mb-4">
              Vincula tu Telegram para usar el bot con esta misma cuenta y el mismo saldo.
            </p>
            {code ? (
              <div className="space-y-2" data-testid="telegram-link-code">
                <div className="text-3xl font-mono tracking-[0.3em] text-white">{code.code}</div>
                <p className="text-xs text-white/55">
                  Abre tu chat con el bot y escribe <span className="text-red-400 font-mono">{code.command}</span>.
                  Caduca en {code.expires_minutes} minutos.
                </p>
                <GlassButton tone="ghost" data-testid="telegram-link-check" onClick={refresh}>
                  Ya lo envié, comprobar
                </GlassButton>
              </div>
            ) : (
              <GlassButton tone="red" icon={Link2} loading={busy} data-testid="telegram-link-btn"
                onClick={getCode}>Vincular Telegram</GlassButton>
            )}
          </>
        )}
      </div>

      <GlassButton tone="ghost" icon={LogOut} data-testid="user-logout-btn" onClick={signOut}>
        Cerrar sesión
      </GlassButton>
    </div>
  );
};

export default UserAccount;
